/**
 * Equipment Selection Handlers - Extracted to reduce file size
 * Contains event handlers and business logic for equipment selection
 */

/**
 * Handle item selection toggle
 */
export const createItemToggleHandler = (setDomainGroups) => (domain, itemId, selected) => {
  setDomainGroups(prev => {
    const updated = { ...prev };
    
    // Find and update the item in optional array
    const optionalIndex = updated[domain].optional.findIndex(item => item.id === itemId);
    if (optionalIndex !== -1) {
      updated[domain].optional[optionalIndex] = {
        ...updated[domain].optional[optionalIndex],
        selected,
        qtyPresent: selected ? (updated[domain].optional[optionalIndex].qtyPresent || 1) : 0
      };
      return updated;
    }
    
    // Find and update the item in wizard-selected array
    const wizardIndex = updated[domain].wizardSelected?.findIndex(item => item.id === itemId);
    if (wizardIndex !== -1 && updated[domain].wizardSelected) {
      updated[domain].wizardSelected[wizardIndex] = {
        ...updated[domain].wizardSelected[wizardIndex],
        selected,
        qtyPresent: selected ? (updated[domain].wizardSelected[wizardIndex].qtyPresent || 1) : 0
      };
      return updated;
    }
    
    return updated;
  });
};

/**
 * Handle required item override
 */
export const createRequiredItemClickHandler = (setOverrideItem, setShowOverrideModal) => (domain, item) => {
  setOverrideItem({ domain, item });
  setShowOverrideModal(true);
};

/**
 * Handle override confirmation
 */
export const createOverrideConfirmHandler = (setDomainGroups, setShowOverrideModal, setOverrideItem) => (overrideData, overrideItem) => {
  if (!overrideItem) return;

  const { domain, item } = overrideItem;
  
  setDomainGroups(prev => {
    const updated = { ...prev };
    const requiredIndex = updated[domain].required.findIndex(req => req.id === item.id);
    
    if (requiredIndex !== -1) {
      updated[domain].required[requiredIndex] = {
        ...updated[domain].required[requiredIndex],
        presence: 'overridden',
        selected: false,
        qtyPresent: 0,
        override: {
          ...overrideData,
          createdAt: new Date().toISOString(),
          createdBy: 'current_user'
        }
      };
    }
    
    return updated;
  });
  
  setShowOverrideModal(false);
  setOverrideItem(null);
};

// ExtrasDrawer handlers removed - user-added equipment now managed via Equipment Register only

/**
 * Handle quantity changes
 */
export const createQuantityChangeHandler = (setDomainGroups, setOverrideItem, setShowOverrideModal) => (domain, itemId, newQuantity, isRequired = false) => {
  setDomainGroups(prev => {
    const updated = { ...prev };
    
    if (isRequired) {
      const requiredIndex = updated[domain].required.findIndex(item => item.id === itemId);
      if (requiredIndex !== -1) {
        const item = updated[domain].required[requiredIndex];
        
        if (newQuantity < item.qtyRequired) {
          setOverrideItem({ domain, item });
          setShowOverrideModal(true);
          return prev;
        }
        
        updated[domain].required[requiredIndex] = {
          ...item,
          qtyPresent: newQuantity
        };
      }
    } else {
      const optionalIndex = updated[domain].optional.findIndex(item => item.id === itemId);
      if (optionalIndex !== -1) {
        updated[domain].optional[optionalIndex] = {
          ...updated[domain].optional[optionalIndex],
          qtyPresent: Math.max(newQuantity, 0)
        };
      } else {
        const wizardIndex = updated[domain].wizardSelected?.findIndex(item => item.id === itemId);
        if (wizardIndex !== -1 && updated[domain].wizardSelected) {
          updated[domain].wizardSelected[wizardIndex] = {
            ...updated[domain].wizardSelected[wizardIndex],
            qtyPresent: Math.max(newQuantity, 0)
          };
        }
      }
    }
    
    return updated;
  });
};

/**
 * Handle variant selection for abstract requirements
 */
export const createVariantSelectedHandler = (setDomainGroups) => async (abstractRequirement, selectedVariant) => {

  try {
    const concreteEquipment = {
      id: `${abstractRequirement.id}_${selectedVariant.id}`,
      name: selectedVariant.name,
      category: abstractRequirement.category || 'LSA',
      domain: abstractRequirement.domain,
      reference: abstractRequirement.reference,
      mandatory: true,
      selected: true,
      qtyPresent: 1,
      qtyRequired: 1,
      description: selectedVariant.description || `${selectedVariant.name} - user selected implementation`,
      source: 'abstract_variant',
      abstractRequirementId: abstractRequirement.id,
      variantId: selectedVariant.id
    };

    setDomainGroups(prevGroups => {
      const updated = { ...prevGroups };
      const domain = abstractRequirement.domain;
      
      if (!updated[domain]) {
        updated[domain] = { required: [], optional: [], abstract: [] };
      }
      
      updated[domain].required = [...(updated[domain].required || []), concreteEquipment];
      
      updated[domain].abstract = (updated[domain].abstract || []).map(abstractReq => {
        if (abstractReq.id === abstractRequirement.id) {
          return {
            ...abstractReq,
            satisfied: true,
            currentInstances: [{
              id: concreteEquipment.id,
              name: selectedVariant.name,
              qtyPresent: 1,
              variantId: selectedVariant.id
            }]
          };
        }
        return abstractReq;
      });
      
      return updated;
    });

  } catch (error) {

    alert('Error saving variant selection. Please try again.');
  }
};

/**
 * Handle save to equipment register
 */
export const createSaveHandler = (setSaving, currentVesselId, transformSelectionsToRegister) => async (domainGroups) => {
  try {
    setSaving(true);
    
    if (!currentVesselId) {
      throw new Error('No vessel selected');
    }

    const { selectedItems, auditEntries } = transformSelectionsToRegister(domainGroups, currentVesselId);

    const storageKey = `equipment_register_${currentVesselId}`;
    localStorage.setItem(storageKey, JSON.stringify(selectedItems));

    if (auditEntries.length > 0) {
      const auditKey = `equipment_audit_${currentVesselId}`;
      const existingAudit = JSON.parse(localStorage.getItem(auditKey) || '[]');
      localStorage.setItem(auditKey, JSON.stringify([...existingAudit, ...auditEntries]));
    }

    window.dispatchEvent(new CustomEvent('equipment:register:updated', {
      detail: {
        vesselId: currentVesselId,
        source: 'equipment_selection',
        itemCount: selectedItems.length,
        auditEntries: auditEntries.length,
        timestamp: new Date().toISOString()
      }
    }));

    alert(`Equipment register updated with ${selectedItems.length} items`);
    
  } catch (err) {

    alert(`Error saving equipment: ${err.message}`);
  } finally {
    setSaving(false);
  }
};